/**
 * Avatar Types
 * Type definitions for the avatar system
 */

import { normalizeAvatarId } from '../../../constants/avatarIds';
import type { AvatarId } from '../../../constants/avatarIds';

/**
 * Avatar mood constants
 * Centralized constants for avatar mood states
 */
export const AVATAR_MOODS = {
  IDLE: 'idle',
  HAPPY: 'happy',
  SAD: 'sad',
} as const;

/**
 * Avatar mood states
 * Derived from AVATAR_MOODS constant for type safety
 */
export type AvatarMood = typeof AVATAR_MOODS[keyof typeof AVATAR_MOODS];

/**
 * Home/away variant for success screen (challenger = home, opponent = away).
 */
export const AVATAR_HOME_AWAY = {
  HOME: 'home',
  AWAY: 'away',
} as const;

export type AvatarHomeAway = (typeof AVATAR_HOME_AWAY)[keyof typeof AVATAR_HOME_AWAY];

/**
 * Avatar registry entry structure
 * Maps each mood to an image source. angry is fallback when sad.png is missing on CDN.
 * home/away used on success screen after creating a challenge.
 */
export type AvatarRegistryEntry = {
  idle: import('react-native').ImageSourcePropType;
  happy: import('react-native').ImageSourcePropType;
  sad: import('react-native').ImageSourcePropType;
  /** Fallback for losing when sad.png does not exist on CDN */
  angry?: import('react-native').ImageSourcePropType;
  /** Challenger (us) on success screen */
  home?: import('react-native').ImageSourcePropType;
  /** Opponent (them) on success screen */
  away?: import('react-native').ImageSourcePropType;
};

/**
 * Avatar IDs that can be displayed (includes reserved e.g. monkey, which users cannot pick).
 */
export type DisplayAvatarId = AvatarId | 'monkey';

/**
 * Map API/legacy avatar strings to a displayable ID (preserves reserved `monkey`).
 */
export function normalizeDisplayAvatarId(avatarId: string | null | undefined): DisplayAvatarId {
  if (avatarId === 'monkey') return 'monkey';
  return normalizeAvatarId(avatarId) as DisplayAvatarId;
}

/**
 * Avatar registry type
 * Maps avatar IDs (user-selectable + display-only e.g. monkey) to their mood-based image paths.
 */
export type AvatarRegistryType = Record<DisplayAvatarId, AvatarRegistryEntry>;

